<template>
    <div :class="[
        'shrink-0 sticky top-0 z-50 bg-white w-full h-[64px] border-b border-gray-DFDFDF',
        'px-[20px] lg:px-[40px] flex items-center justify-between font-oceanside',
    ]">
        <reset-panel-button
            class="w-[18px]"
        />
        <h1
            class="flex-1 text-center text-base -mr-[19px] pointer-events-none flex flex-col font-semibold"
        >
            {{ $t('PAINTFILL') }}
        </h1>
        <close-button
            width="18.84"
            @click="handleClose"
        />
    </div>

    <placement-header-alt
        v-if="!isMobile"
        :placement="visiblePlacementsPaintfill?.[0]"
        :placementData="placementData"
        :hide-combine="true"
    />

    <mobile-product-renderer v-if="isMobile" />

    <div class="sticky top-0 z-10 -mt-[1px]">
        <step-options-color-controls :is-active="true" />
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-[20px] font-oceanside px-[20px] lg:px-[40px] relative pt-[24px]">
        <step-options-initials-buttons
            :main-placement="visiblePlacementsPaintfill?.[0]"
            :option="paintfillOption"
            :has-add-remove-button="true"
            :remove-selected-on-add-remove-click="true"
            :has-image="!isMobile"
            :navigate-on-click="false"
            @unselect="openRemoveConfirmationModal"
            background-size="auto 100%"
        />
        <step-options-front-paintfill-list
            :visible-placements="visiblePlacementsPaintfill"
            :has-color-error-state="effectiveHasPaintfillColorErrorState"
            @updateColorIsSelected="handleUpdateColorIsSelected"
        />
    </div>

    <step-options-apply-selections
        class="shrink-0"
        @apply-selections="handleClose"
        @handle-disabled="handleDisabled"
        :is-disabled="isDisabled"
        :should-show-paintfill-requirement="shouldShowPaintfillRequirement"
        :is-dirty="effectiveIsDirty"
        :should-close-group-overlay="false"
        :button-text="$t('APPLY_PAINTFILL')"
    />

    <transition name="cz3__fade">
        <teleport to="#cz3" v-if="shouldShowPaintfillModal || showRemoveConfirmationModal">
            <base-modal-confirm
                v-if="shouldShowPaintfillModal"
                @modalClose="closePaintfillModal"
                @confirmAndExit="closePaintfillModal"
                :show-modal="shouldShowPaintfillModal"
                :modal-copy="$t('PAINTFILL_CUSTOMIZATION_COPY')"
                :modal-heading="$t('PAINTFILL_CUSTOMIZATION')"
                :cta-copy="$t('CONTINUE')"
                :show-cancel-button="false"
            />
            <base-modal-confirm
                v-if="showRemoveConfirmationModal"
                @modalClose="closeRemoveConfirmationModal"
                @confirmAndExit="handleRemoveSelection"
                :show-modal="showRemoveConfirmationModal"
                :modal-copy="$t('RESET_PAINTFILL_CONFIRMATION_COPY')"
                :modal-heading="$t('RESET_PAINTFILL_CONFIRMATION')"
                :cta-copy="$t('REMOVE')"
                :show-cancel-button="true"
            />
        </teleport>
    </transition>
</template>

<script>
    import allStores from '../stores/all';
    import BaseModalConfirm from './BaseModalConfirm.vue';
    import CloseButton from './buttons/CloseButton.vue';
    import ResetPanelButton from './buttons/ResetPanelButton.vue';
    import StepOptionsApplySelections from './StepOptionsApplySelections.vue';
    import StepOptionsColorControls from './StepOptionsColorControls.vue';
    import StepOptionsFrontPaintfillList from './StepOptionsFrontPaintfillList.vue';
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import MobileProductRenderer from './MobileProductRenderer.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';

    export default {
        name: 'StepOptionsFrontPaintfillOptions',

        mixins: [allStores],

        components: {
            BaseModalConfirm,
            CloseButton,
            ResetPanelButton,
            StepOptionsApplySelections,
            StepOptionsColorControls,
            StepOptionsFrontPaintfillList,
            StepOptionsInitialsButtons,
            MobileProductRenderer,
            PlacementHeaderAlt,
        },

        props: {
            visiblePlacementsPaintfill: {
                type: Array,
                required: true,
            },

            hasPaintfillColorErrorState: {
                type: Boolean,
                default: false,
            },

            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                shouldShowPaintfillModal: false,
                showRemoveConfirmationModal: false,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            isMobile() {
                return this.appStore?.isMobile;
            },

            placementData() {
                // only show painfill-summary-link
                return {
                    name: 'Face/Sole',
                    code: 'Face/Sole',
                    placements: [
                        {
                            name: 'Paintfill',
                            code: 'PutterFrontPaintfill',
                        },
                    ],
                };
            },

            enableFocusLock() {
                return this.appStore.activeNestedPanel?.panelId === 'frontPaintfillOptions';
            },

            effectiveIsDirty() {
                return this.isDirty || this.hasAttemptedApply;
            },

            hasAttemptedApply() {
                return this.customizerStore.hasFrontNestedPanelValidationAttempted('paintfill');
            },

            effectiveHasPaintfillColorErrorState() {
                return this.hasPaintfillColorErrorState || (this.effectiveIsDirty && !this.isComplete);
            },

            isComplete() {
                return this.customizerStore.isPaintfillComplete('Face/Sole');
            },

            isDisabled() {
                return !this.isComplete;
            },

            shouldShowPaintfillRequirement() {
                return !this.isComplete;
            },

            paintfillOption() {
                const stampPlacement = this.visiblePlacementsPaintfill.find((x) => x.code === 'PutterFrontPaintfill');

                const component = stampPlacement?.components?.find((x) => !x.placeholder);

                if (!component) {
                    return null;
                }

                return {
                    selected: true,
                    label: 'Paintfill',
                    value: component.code,
                    raw: {
                        ...component,
                        custom: {
                            ...(component.custom || {}),
                            ...{
                                'v-thumbnail-asset': 'PutterFace-PaintfillSelected',
                            },
                        },
                    },
                };
            },
        },

        watch: {
            isComplete(value) {
                if (value) {
                    this.customizerStore.clearFrontNestedPanelValidationAttempted('paintfill');
                }
            },
        },

        mounted() {
            this.maybeShowPaintfillModal();
            this.customizerStore.setActiveView('Sole');
        },

        methods: {
            openRemoveConfirmationModal() {
                this.showRemoveConfirmationModal = true;
            },

            handleRemoveSelection() {
                this.customizerStore.resetAllPaintfill();
                this.appStore.closeNestedPanel();
                this.customizerStore.setActiveView('Sole');
            },

            handleUpdateColorIsSelected() {
                this.customizerStore.setActiveView('Sole');
            },

            maybeShowPaintfillModal() {
                const storageKey = 'frontPaintfillModalShown';

                if (sessionStorage.getItem(storageKey)) {
                    return;
                }

                this.shouldShowPaintfillModal = true;
                sessionStorage.setItem(storageKey, 'true');
            },

            closePaintfillModal() {
                this.shouldShowPaintfillModal = false;
            },

            closeRemoveConfirmationModal() {
                this.showRemoveConfirmationModal = false;
            },

            handleDisabled() {
                this.customizerStore.setFrontNestedPanelValidationAttempted('paintfill', true);
            },

            handleClose() {
                this.appStore.closeNestedPanel();
                this.customizerStore.setActiveView('Sole');
            },
        },
    };
</script>
